import {
  Attendance,
  attendanceAsUserAttendance,
  attendanceDtoAsDomain,
  AttendancePair,
} from "../../domain/attendance/Attendance";
import UserRepository from "./UserRepository";
import {
  collection,
  doc,
  DocumentSnapshot,
  getDoc,
  getDocFromCache,
  onSnapshot,
  orderBy as firestoreOrderBy,
  query,
  QueryConstraint,
  startAfter,
  Timestamp,
  Unsubscribe,
  where,
} from "firebase/firestore";
import FirestoreSource from "../source/FirestoreSource";
import { AttendanceStoreDao } from "../database/dao/Attendance";

import { isEmpty, isNil } from "lodash";
import { DateTime } from "luxon";
import { AppTimestampDao } from "../database/dao/AppTimestamp";
import { FirestoreSimpleCrudSource } from "../source/FirestoreSimpleCrudSource";
import { AttendanceDto } from "../../network/attendance/Attendance";
import { AppTimestamp } from "../../domain/app/Timestamp";
import UnityRepository from "./UnityRepository";
import { AccountTypeRepository } from "./AccountTypeRepository";
import ShiftRepository from "./ShiftRepository";
import { UserWrapper } from "../../domain/user/User";
import Fuse from "fuse.js";

export class AttendanceRepository {
  private static unsubscribeEntries: Unsubscribe | null;
  private static unsubscribeDepartures: Unsubscribe | null;

  private static entriesSource = new FirestoreSimpleCrudSource<AttendanceDto>(
    "entry"
  );

  private static departureSource = new FirestoreSimpleCrudSource<AttendanceDto>(
    "departure"
  );

  static async getAttendance(entryReference: string): Promise<AttendancePair> {
    let entry = await AttendanceStoreDao.getEntryByReference(entryReference);
    if (isNil(entry)) throw new Error("El ingreso no fue encontrado.");
    return await this.doPair(entry);
  }

  static async getList(
    timestamp: number,
    unityId: number,
    update?: boolean
  ): Promise<
    { pairs: AttendancePair[]; timestamp?: AppTimestamp } | undefined
  > {
    const dateTime = DateTime.fromMillis(timestamp).setZone("America/Lima");
    const dayStartTimestamp = dateTime.startOf("day").toMillis();
    const dayEndTimestamp = dateTime.endOf("day").toMillis();
    const appTimestampKey = `${unityId}/${dayStartTimestamp}/${dayEndTimestamp}`;
    let appTimestamp = await AppTimestampDao.getTimestamp(appTimestampKey);
    let entries = await AttendanceStoreDao.getAllUnityEntries(
      unityId,
      dayStartTimestamp,
      dayEndTimestamp
    );
    if (isEmpty(entries) || update) {
      await AttendanceStoreDao.deleteUnityEntries(
        unityId,
        dayStartTimestamp,
        dayEndTimestamp
      );
      await AttendanceStoreDao.deleteUnityDepartures(
        unityId,
        dayStartTimestamp,
        dayEndTimestamp
      );

      await this.fetchRemoteAttendanceList(
        dayStartTimestamp,
        dayEndTimestamp,
        [where("unityId", "==", unityId)],
        [where("unityId", "==", unityId)]
      );

      await AppTimestampDao.putTimestamp({
        key: appTimestampKey,
        timestamp: Date.now(),
      });
      appTimestamp = await AppTimestampDao.getTimestamp(appTimestampKey);
      entries = await AttendanceStoreDao.getAllUnityEntries(
        unityId,
        dayStartTimestamp,
        dayEndTimestamp
      );
    }
    const pairs = await this.doPairs(entries);
    return {
      pairs,
      timestamp: appTimestamp,
    };
  }

  static async getGlobalList(
    timestamp: number,
    forceRefresh: boolean = false,
    appUser: UserWrapper
  ): Promise<
    { pairs: AttendancePair[]; timestamp?: AppTimestamp } | undefined
  > {
    if (appUser?.isClient() || appUser?.isSupervisor()) {
      if (appUser!.user.clientUnity) {
        const clientUnityConstraint = [
          where("unityId", "in", appUser.user.clientUnity),
        ];

        return this.getGlobalListWithConstraints(
          timestamp,
          forceRefresh,
          clientUnityConstraint,
          clientUnityConstraint
        );
      }
    } else {
      return this.getGlobalListWithConstraints(timestamp, forceRefresh);
    }
  }

  static async getLiveList(
    entry: Attendance,
    appUser: UserWrapper
  ): Promise<AttendancePair> {
    const dateTime = DateTime.fromMillis(
      DateTime.now().toJSDate().getTime()
    ).setZone("America/Lima");
    const entryAttendanceEntry = await AttendanceRepository.injectAttendance(
      entry
    );
    let departure: Attendance | null = null;

    let result: AttendancePair = {
      entry: entryAttendanceEntry,
    };

    if (appUser?.isClient() || appUser?.isSupervisor()) {
      if (appUser!.user!.clientUnity) {
        const clientUnityConstraint = [
          where("unityId", "in", appUser!.user!.clientUnity),
        ];
        departure = await this.fetchLiveDeparture(entry, [
          where("ownerUid", "==", entry.ownerUid),
          ...clientUnityConstraint,
        ]);
      }
    } else {
      departure = await this.fetchLiveDeparture(entry, [
        where("ownerUid", "==", entry.ownerUid),
      ]);
    }

    if (departure) {
      result.departure = await this.injectAttendance(departure);
    } else {
      console.log("departure no pass", departure);
    }

    // const pairs = await this.doPair(entry);
    // return {
    //   entry: entry,
    //   // departure: departure?.data,
    // };
    return result;
  }

  static async getLiveListDeparture(entry: Attendance) {
    let result: AttendancePair = {
      entry: entry,
    };

    let departure: Attendance | null = null;

    departure = await this.fetchLiveDeparture(entry, [
      where("ownerUid", "==", entry.ownerUid),
    ]);

    if (departure) {
      result.departure = await this.injectAttendance(departure);
    } else {
      console.log("departure no pass", departure);
    }
  }

  static async getUserAttendance(
    uid: string,
    timestamp: number,
    forceRefresh: boolean = false,
    appUser: UserWrapper
  ): Promise<AttendancePair | undefined> {
    if (appUser?.isClient() || appUser?.isSupervisor()) {
      if (appUser!.user!.clientUnity) {
        const clientUnityConstraint = [
          where("unityId", "in", appUser.user.clientUnity),
        ];
        return await this.getUserAttendanceWithConstraints(
          uid,
          timestamp,
          forceRefresh,
          clientUnityConstraint,
          clientUnityConstraint
        );
      }
    } else {
      return await this.getUserAttendanceWithConstraints(
        uid,
        timestamp,
        forceRefresh
      );
    }
    return undefined;
  }

  static async getLive(
    onError: (error: Error) => void,
    appUser: UserWrapper
  ): Promise<Unsubscribe | undefined> {
    if (this.unsubscribeEntries) return undefined;
    const dayStartTimestamp = DateTime.now()
      .setZone("America/Lima")
      .startOf("day")
      .toMillis();
    let lastDayEntry = await AttendanceStoreDao.getLastEntryAboveTimestamp(
      dayStartTimestamp
    );
    let lastDocument: DocumentSnapshot | undefined;
    if (lastDayEntry) {
      try {
        lastDocument = await getDocFromCache(
          doc(FirestoreSource.firestore, lastDayEntry.reference)
        );
      } catch (e: any) {
        lastDocument = await getDoc(
          doc(FirestoreSource.firestore, lastDayEntry.reference)
        );
      }
    }
    const entryCollection = collection(FirestoreSource.firestore, "entry");
    // const departureCollection = collection(
    //   FirestoreSource.firestore,
    //   "departure"
    // );

    const constraints: QueryConstraint[] = [
      firestoreOrderBy("timestamp", "asc"),
    ];
    if (!!lastDocument) constraints.push(startAfter(lastDocument));
    if (appUser.isClient() && appUser.user.clientUnity)
      constraints.push(where("unityId", "in", appUser.user.clientUnity));
    let entryQuery = query(entryCollection, ...constraints);
    this.unsubscribeEntries = onSnapshot(
      entryQuery,
      {
        includeMetadataChanges: false,
      },
      async (snapshot) => {
        for (let documentSnapshot of snapshot.docs) {
          const attendance = attendanceDtoAsDomain(
            documentSnapshot.data() as AttendanceDto,
            documentSnapshot.ref.path
          );
          await AttendanceStoreDao.putEntry(attendance);
        }
      },
      (error) => {
        onError(error);
      }
    );
    return this.unsubscribeEntries;
  }

  static async getLiveDepartures(
    onError: (error: Error) => void,
    appUser: UserWrapper
  ): Promise<Unsubscribe | undefined> {
    if (this.unsubscribeDepartures) return undefined;
    const dayStartTimestamp = DateTime.now()
      .setZone("America/Lima")
      .startOf("day")
      .toMillis();
    let lastDayDeparture =
      await AttendanceStoreDao.getLastDepartureAboveTimestamp(
        dayStartTimestamp
      );
    let lastDocument: DocumentSnapshot | undefined;
    if (lastDayDeparture) {
      try {
        lastDocument = await getDocFromCache(
          doc(FirestoreSource.firestore, lastDayDeparture.reference)
        );
      } catch (e: any) {
        lastDocument = await getDoc(
          doc(FirestoreSource.firestore, lastDayDeparture.reference)
        );
      }
    }
    const departureCollection = collection(
      FirestoreSource.firestore,
      "departure"
    );

    const constraints: QueryConstraint[] = [
      firestoreOrderBy("timestamp", "asc"),
    ];
    if (!!lastDocument) constraints.push(startAfter(lastDocument));
    if (appUser.isClient() && appUser.user.clientUnity)
      constraints.push(where("unityId", "in", appUser.user.clientUnity));
    let departureQuery = query(departureCollection, ...constraints);
    this.unsubscribeDepartures = onSnapshot(
      departureQuery,
      {
        includeMetadataChanges: false,
      },
      async (snapshot) => {
        for (let documentSnapshot of snapshot.docs) {
          const attendance = attendanceDtoAsDomain(
            documentSnapshot.data() as AttendanceDto,
            documentSnapshot.ref.path
          );
          await AttendanceStoreDao.putDeparture(attendance);
        }
      },
      (error) => {
        onError(error);
      }
    );
    return this.unsubscribeDepartures;
  }

  static async stopLive() {
    if (this.unsubscribeEntries) {
      this.unsubscribeEntries();
      this.unsubscribeEntries = null;
    }
  }

  static async doPairs(entryArray: Attendance[]): Promise<AttendancePair[]> {
    return await Promise.all(
      entryArray.map(async (entry) => {
        return await this.doPair(entry);
      })
    );
  }

  static async doPair(entry: Attendance): Promise<AttendancePair> {
    let attendanceEntry = await this.injectAttendance(entry);
    let result: AttendancePair = {
      entry: attendanceEntry!!,
    };

    const departure = await AttendanceStoreDao.getDeparture(
      entry.ownerUid,
      entry.timestamp,
      entry.timestamp + 14 * 60 * 60 * 1000
    );

    if (departure) {
      result.departure = await this.injectAttendance(departure);
    } else {
      console.log("departure false", departure);
    }

    return result;
  }

  static async injectAttendance(currentAttendance: Attendance) {
    let user = await UserRepository.getUser(currentAttendance.ownerUid);
    let currentAttendanceUnity = await UnityRepository.getUnity(
      currentAttendance.unityId
    );
    let currentAttendanceAccountType = !isNil(currentAttendance.accountTypeId)
      ? await AccountTypeRepository.getAccountType(
          currentAttendance.accountTypeId
        )
      : undefined;
    let currentAttendanceShift = !isNil(currentAttendance.shiftId)
      ? await ShiftRepository.getShift(currentAttendance.shiftId)
      : undefined;
    return attendanceAsUserAttendance(
      currentAttendance,
      user,
      currentAttendanceUnity,
      currentAttendanceAccountType,
      currentAttendanceShift
    );
  }

  private static async getGlobalListWithConstraints(
    timestamp: number,
    forceRefresh: boolean = false,
    entryConstraints: QueryConstraint[] = [],
    departureConstraints: QueryConstraint[] = []
  ): Promise<
    { pairs: AttendancePair[]; timestamp?: AppTimestamp } | undefined
  > {
    const dateTime = DateTime.fromMillis(timestamp).setZone("America/Lima");
    const dayStartTimestamp = dateTime.startOf("day").toMillis();
    const dayEndTimestamp = dateTime.endOf("day").toMillis();
    const appTimestampKey = `attendance-joined/${dayStartTimestamp}/${dayEndTimestamp}`;
    let appTimestamp = await AppTimestampDao.getTimestamp(appTimestampKey);
    let entries: Attendance[] | null = null;

    // await AttendanceStoreDao.getAllEntries(
    //   dayStartTimestamp,
    //   dayEndTimestamp
    // );

    if (isEmpty(entries) || forceRefresh) {
      await AttendanceStoreDao.deleteEntries(
        dayStartTimestamp,
        dayEndTimestamp
      );
      await AttendanceStoreDao.deleteDepartures(
        dayStartTimestamp,
        dayEndTimestamp
      );
      await this.fetchRemoteAttendanceList(
        dayStartTimestamp,
        dayEndTimestamp,
        entryConstraints,
        departureConstraints
      );
      await AppTimestampDao.putTimestamp({
        key: appTimestampKey,
        timestamp: Date.now(),
      });
      appTimestamp = await AppTimestampDao.getTimestamp(appTimestampKey);
      entries = await AttendanceStoreDao.getAllEntries(
        undefined,
        undefined,
        dayStartTimestamp,
        dayEndTimestamp
      );
    } else {
      entries = await AttendanceStoreDao.getAllEntries(
        undefined,
        undefined,
        dayStartTimestamp,
        dayEndTimestamp
      );
    }
    const pairs = await this.doPairs(entries);
    return {
      pairs,
      timestamp: appTimestamp,
    };
  }

  private static async getUserAttendanceWithConstraints(
    uid: string,
    timestamp: number,
    forceRefresh: boolean = false,
    entryConstraints: QueryConstraint[] = [],
    departureConstraints: QueryConstraint[] = []
  ): Promise<AttendancePair | undefined> {
    const datetime = DateTime.fromMillis(timestamp).setZone("America/Lima");
    const dateStart = datetime.startOf("day").toMillis();
    const dateEnd = datetime.endOf("day").toMillis();
    let localEntry = await AttendanceStoreDao.getEntry(uid, dateStart, dateEnd);
    if (!localEntry || forceRefresh) {
      await this.fetchRemoteAttendanceList(
        dateStart,
        dateEnd,
        [where("ownerUid", "==", uid), ...entryConstraints],
        [where("ownerUid", "==", uid), ...departureConstraints]
      );
      localEntry = await AttendanceStoreDao.getEntry(uid, dateStart, dateEnd);
    }
    if (!!localEntry) {
      return await this.doPair(localEntry);
    }
  }

  private static async fetchRemoteAttendanceList(
    dateStartMillis: number,
    dateEndMillis: number,
    entryConstraints: QueryConstraint[] = [],
    departureConstraints: QueryConstraint[] = []
  ): Promise<void> {
    const remoteEntry = await this.entriesSource.getList([
      where("timestamp", ">=", Timestamp.fromMillis(dateStartMillis)),
      where("timestamp", "<=", Timestamp.fromMillis(dateEndMillis)),
      firestoreOrderBy("timestamp", "desc"),
      ...entryConstraints,
    ]);

    if (!!remoteEntry) {
      console.log("remoteEntry", remoteEntry);
      const entries = remoteEntry.map((it) =>
        attendanceDtoAsDomain(it.data, it.reference)
      );
      const departuresTasks = entries.map(async (it) => {
        const dps = await this.departureSource.getByQuery([
          where("timestamp", ">=", Timestamp.fromMillis(it.timestamp)),
          //Hasta 14 horas después
          where(
            "timestamp",
            "<=",
            Timestamp.fromMillis(it.timestamp + 14 * 60 * 60 * 1000)
          ),
          where("ownerUid", "==", it.ownerUid),
          ...departureConstraints,
        ]);
        if (!!dps) {
          console.log("remoteDeparturesNotNull", dps.data);
          return attendanceDtoAsDomain(dps.data, dps.reference);
        }
        if (dps == null) {
          console.log("remoteDeparturesNull", "fk");
        }
      });
      const departures = (await Promise.all(departuresTasks)).filter(
        (it) => !!it
      ) as Array<Attendance>;
      await AttendanceStoreDao.putEntry(...entries);
      if (!!departures && departures.length > 0) {
        await AttendanceStoreDao.putDeparture(...departures);
      }
    }
  }

  private static async fetchLiveDeparture(
    entry: Attendance,
    departureConstraints: QueryConstraint[] = []
  ) {
    const departure = await this.departureSource.getByQuery([
      where("timestamp", ">=", Timestamp.fromMillis(entry.timestamp)),
      //Hasta 14 horas después
      where(
        "timestamp",
        "<=",
        Timestamp.fromMillis(entry.timestamp + 14 * 60 * 60 * 1000)
      ),
      where("ownerUid", "==", entry.ownerUid),
      ...departureConstraints,
    ]);

    if (!!departure) {
      return attendanceDtoAsDomain(departure.data, departure.reference);
    }

    return departure;
  }

  static async searchAttendance(
    filter: string,
    keyword: string,
    timestampStart: number | null,
    timestampEnd: number | null,
    inall?: boolean // : Promise<UnityAttendance[] | undefined>
  ) {
    // let options;
    let localVehicles: Attendance[] | AttendancePair[];
    let dayStartTimestamp;
    let dayEndTimestamp;
    if (timestampStart && timestampEnd) {
      const dateTimeInit =
        DateTime.fromMillis(timestampStart).setZone("America/Lima");
      const dateTimeEnd =
        DateTime.fromMillis(timestampEnd).setZone("America/Lima");

      dayStartTimestamp = dateTimeInit.startOf("day").toMillis();
      dayEndTimestamp = dateTimeEnd.endOf("day").toMillis();
    }

    let vehicleControlList = await AttendanceStoreDao.getAllEntries(
      undefined,
      undefined,
      inall === true ? undefined : dayStartTimestamp,
      inall === true ? undefined : dayEndTimestamp
    );

    localVehicles = await this.doPairs(vehicleControlList);

    // ["unity.label", "operador"]
    let options = {
      shouldSort: true,
      threshold: 0.2,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys:
        filter === "unityId"
          ? ["entry.unity.label"]
          : filter === "operator"
          ? ["entry.owner.displayName"]
          : ["entry.unity.label", "entry.owner.displayName"],
    };

    if (localVehicles) {
      const fuse = new Fuse(localVehicles, options);
      return fuse.search(keyword).map((it) => ({ ...it.item }));
    }
  }

  static async filterAttendance(
    filter: string,
    keyword: string,
    timestampStart: number | null,
    timestampEnd: number | null,
    inall?: boolean // : Promise<UnityAttendance[] | undefined>
  ) {
    // let options;
    let localVehicles: Attendance[] | AttendancePair[];
    let dayStartTimestamp;
    let dayEndTimestamp;
    if (timestampStart && timestampEnd) {
      const dateTimeInit =
        DateTime.fromMillis(timestampStart).setZone("America/Lima");
      const dateTimeEnd =
        DateTime.fromMillis(timestampEnd).setZone("America/Lima");

      dayStartTimestamp = dateTimeInit.startOf("day").toMillis();
      dayEndTimestamp = dateTimeEnd.endOf("day").toMillis();
    }

    let vehicleControlList = await AttendanceStoreDao.getAllEntries(
      undefined,
      undefined,
      inall === true ? undefined : dayStartTimestamp,
      inall === true ? undefined : dayEndTimestamp
    );

    localVehicles = await this.doPairs(vehicleControlList);

    // ["unity.label", "operador"]
    let options = {
      shouldSort: true,
      threshold: 0.2,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 1,
      keys:
        filter === "unityId"
          ? ["entry.unity.label"]
          : filter === "operator"
          ? ["entry.owner.displayName"]
          : ["entry.unity.label", "entry.owner.displayName"],
    };

    if (localVehicles) {
      const fuse = new Fuse(localVehicles, options);
      return fuse.search(keyword).map((it) => ({ ...it.item }));
    }
  }
}
